/** Axis-aligned marquee rect in stage coordinates. */
export interface MarqueeRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** SolidWorks-style: L→R = window (fully inside), R→L = crossing (any touch). */
export type MarqueeMode = 'window' | 'crossing';

export interface MarqueeSelectableBounds {
  x: number;
  y: number;
  width: number;
  height: number;
  /** Degrees; CSS-style rotation about transform-origin. */
  rotation?: number;
  /** Normalized transform-origin (0–1). Default center. */
  originX?: number;
  originY?: number;
}

export function marqueeModeFromDrag(startX: number, currentX: number): MarqueeMode {
  return currentX >= startX ? 'window' : 'crossing';
}

export function buildMarqueeRect(
  startX: number,
  startY: number,
  currentX: number,
  currentY: number
): MarqueeRect {
  const left = Math.min(startX, currentX);
  const top = Math.min(startY, currentY);
  return {
    x: left,
    y: top,
    width: Math.abs(currentX - startX),
    height: Math.abs(currentY - startY),
  };
}

function toRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Four corners of a CSS-rotated box (about transform-origin). */
export function getRotatedRectCorners(bounds: MarqueeSelectableBounds): Array<{ x: number; y: number }> {
  const {
    x,
    y,
    width,
    height,
    rotation = 0,
    originX = 0.5,
    originY = 0.5,
  } = bounds;
  const ox = x + originX * width;
  const oy = y + originY * height;
  const local = [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ];

  if (!rotation) return local;

  const rad = toRadians(rotation);
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return local.map((p) => {
    const dx = p.x - ox;
    const dy = p.y - oy;
    return {
      x: ox + dx * cos - dy * sin,
      y: oy + dx * sin + dy * cos,
    };
  });
}

function pointInAabb(px: number, py: number, rect: MarqueeRect): boolean {
  return (
    px >= rect.x &&
    px <= rect.x + rect.width &&
    py >= rect.y &&
    py <= rect.y + rect.height
  );
}

/** Point inside CSS-rotated object box (about transform-origin). */
function pointInRotatedBounds(px: number, py: number, bounds: MarqueeSelectableBounds): boolean {
  const {
    x,
    y,
    width,
    height,
    rotation = 0,
    originX = 0.5,
    originY = 0.5,
  } = bounds;
  const ox = x + originX * width;
  const oy = y + originY * height;
  let lx = px - ox;
  let ly = py - oy;
  if (rotation) {
    const rad = toRadians(-rotation);
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const rx = lx * cos - ly * sin;
    const ry = lx * sin + ly * cos;
    lx = rx;
    ly = ry;
  }
  // Unrotated local coords relative to top-left
  const localX = lx + originX * width;
  const localY = ly + originY * height;
  return localX >= 0 && localX <= width && localY >= 0 && localY <= height;
}

function segmentsIntersect(
  a1: { x: number; y: number },
  a2: { x: number; y: number },
  b1: { x: number; y: number },
  b2: { x: number; y: number }
): boolean {
  const cross = (
    p: { x: number; y: number },
    q: { x: number; y: number },
    r: { x: number; y: number }
  ) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);

  const onSegment = (
    p: { x: number; y: number },
    q: { x: number; y: number },
    r: { x: number; y: number }
  ) =>
    Math.min(p.x, r.x) <= q.x &&
    q.x <= Math.max(p.x, r.x) &&
    Math.min(p.y, r.y) <= q.y &&
    q.y <= Math.max(p.y, r.y);

  const d1 = cross(a1, a2, b1);
  const d2 = cross(a1, a2, b2);
  const d3 = cross(b1, b2, a1);
  const d4 = cross(b1, b2, a2);

  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }
  if (d1 === 0 && onSegment(a1, b1, a2)) return true;
  if (d2 === 0 && onSegment(a1, b2, a2)) return true;
  if (d3 === 0 && onSegment(b1, a1, b2)) return true;
  if (d4 === 0 && onSegment(b1, a2, b2)) return true;
  return false;
}

function rotatedBoundsIntersectsMarquee(
  bounds: MarqueeSelectableBounds,
  marquee: MarqueeRect
): boolean {
  const corners = getRotatedRectCorners(bounds);
  if (corners.some((c) => pointInAabb(c.x, c.y, marquee))) return true;

  const marqueeCorners = [
    { x: marquee.x, y: marquee.y },
    { x: marquee.x + marquee.width, y: marquee.y },
    { x: marquee.x + marquee.width, y: marquee.y + marquee.height },
    { x: marquee.x, y: marquee.y + marquee.height },
  ];
  if (marqueeCorners.some((c) => pointInRotatedBounds(c.x, c.y, bounds))) return true;

  for (let i = 0; i < corners.length; i++) {
    const a1 = corners[i]!;
    const a2 = corners[(i + 1) % corners.length]!;
    for (let j = 0; j < marqueeCorners.length; j++) {
      const b1 = marqueeCorners[j]!;
      const b2 = marqueeCorners[(j + 1) % marqueeCorners.length]!;
      if (segmentsIntersect(a1, a2, b1, b2)) return true;
    }
  }

  return false;
}

export function isBoundsInMarquee(
  bounds: MarqueeSelectableBounds,
  marquee: MarqueeRect,
  mode: MarqueeMode
): boolean {
  if (marquee.width <= 0 && marquee.height <= 0) return false;

  if (mode === 'window') {
    return getRotatedRectCorners(bounds).every((c) => pointInAabb(c.x, c.y, marquee));
  }

  return rotatedBoundsIntersectsMarquee(bounds, marquee);
}

export function collectIdsInMarquee<T extends MarqueeSelectableBounds & { id: string }>(
  objects: T[],
  marquee: MarqueeRect,
  mode: MarqueeMode,
  isEligible: (obj: T) => boolean
): string[] {
  const ids: string[] = [];
  for (const obj of objects) {
    if (!isEligible(obj)) continue;
    if (isBoundsInMarquee(obj, marquee, mode)) ids.push(obj.id);
  }
  return ids;
}
